<script lang="ts" setup>
import type { NovoIconNames } from '@novonordisk/studyhub-novoui/vue'
import { NIcon } from '@novonordisk/studyhub-novoui/vue'
import { computed } from 'vue'

const { variant } = defineProps<{
  disabled: boolean
  hasErrors: boolean
  active: boolean
  icon?: NovoIconNames
  loading?: boolean
  size: 'small' | 'medium'
  clearable?: boolean
  value: unknown
  variant?: 'default' | 'light'
}>()

const emit = defineEmits<{
  (e: 'click:clear', value: MouseEvent): void
}>()

const baseClass = computed((): string => {
  return variant === 'light' ? 'bg-general-base' : 'bg-general-low-emphasis-enabled'
})
</script>

<template>
  <button
    data-test="input-activator"
    :class="{
      [`border-general-medium ${baseClass} ring-0 ring-general-high-emphasis-enabled hover:border-general-high-emphasis-enabled focus:border-general-high-emphasis-enabled focus:bg-general-medium-emphasis-hover`]:
        !disabled && !active && !hasErrors,
      'border-general-high-emphasis-enabled bg-general-medium-emphasis-hover ring-1 ring-general-high-emphasis-enabled hover:border-general-high-emphasis-enabled focus:border-general-high-emphasis-enabled':
        !disabled && active && !hasErrors,
      'border-alert-enabled bg-alert-low-emphasis-enabled ring-0 ring-alert-enabled': !disabled && !active && hasErrors,
      'border-alert-enabled bg-alert-low-emphasis-enabled ring-1 ring-alert-enabled': !disabled && active && hasErrors,
      'cursor-not-allowed border-[#ada7a1] bg-general-low-emphasis-enabled': disabled,
      'invalid-input input-invalid': hasErrors,
      'h-10 text-ui-input-14': size === 'small',
      'h-12 text-ui-input-16': size === 'medium',
    }"
    :disabled="disabled"
    class="group flex w-full items-center justify-between rounded border px-4 py-3 text-left text-general-primary ring-inset transition-colors duration-100 ease-in-out focus:outline-0 focus:ring-1"
  >
    <slot />
    <span class="flex items-center gap-2">
      <button
        v-if="
          clearable &&
          value !== null &&
          value !== undefined &&
          value !== '' &&
          !(Array.isArray(value) && value.length === 0)
        "
        data-test="clear-button"
        @click.stop="emit('click:clear', $event)"
      >
        <NIcon
          data-test="clear-icon"
          icon="closeSmall"
          class="opacity-0 transition-opacity duration-100 ease-in-out group-hover:opacity-100"
          :size="18"
        />
      </button>
      <VLoader
        v-if="loading"
        data-test="loader"
        class="mx-0! shrink-0"
        width="20px"
      />
      <NIcon
        v-else
        data-test="activator-icon"
        :class="{
          'fill-disabled-medium': disabled,
          'fill-alert-enabled': hasErrors,
          'fill-general-primary': !disabled && !hasErrors,
          // Fix for icon alignment
          '-mt-0.5': icon === 'chevronDown',
        }"
        :icon="icon"
        :size="20"
        class="shrink-0"
      />
    </span>
  </button>
</template>
